import gql from 'graphql-tag';

export enum DashboardTrendGranularity {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY'
}

export enum BranchPerformanceMetric {
  REVENUE = 'REVENUE',
  ORDERS = 'ORDERS',
  VOLUME = 'VOLUME',
  ON_TIME_RATE = 'ON_TIME_RATE'
}

export interface DashboardFilterInput {
  startDate: string;
  endDate: string;
  branchId?: number;
}

export interface DashboardMetric {
  value: number;
  previousValue: number;
  deltaPercentage: number | null;
}

export interface DashboardVolumeBreakdown {
  gallon: number;
  smallpack: number;
}

export interface DashboardTimelinessBucket {
  label: string;
  count: number;
  percentage: number;
}

export interface DashboardBusinessHealth {
  totalOrders: DashboardMetric;
  totalVolume: DashboardMetric;
  volumeBreakdown: DashboardVolumeBreakdown;
  revenue: DashboardMetric;
  activeCustomers: DashboardMetric;
  returningCustomerRate: DashboardMetric;
  timeliness: DashboardTimelinessBucket[];
  timelinessUncalculatedOrders: number;
}

export interface DashboardBranchPerformance {
  branchId: number;
  branchName: string;
  revenue: number;
  orders: number;
  volume: number;
  onTimeRate: number;
  finishedOrders: number;
}

export interface DashboardOverview {
  businessHealth: DashboardBusinessHealth;
  branchPerformance: DashboardBranchPerformance[];
  generatedAt: string;
}

export interface DashboardTrendPoint {
  label: string;
  orders: number;
  revenue: number;
}

export interface DashboardTodaySnapshot {
  totalScheduledOrders: number;
  deliveredOnTime: number;
  pendingToday: number;
  unfinished: number;
  generatedAt: string;
}

export interface FetchDashboardOverviewQueryResult {
  dashboardOverview: DashboardOverview;
}

export interface FetchDashboardTrendQueryResult {
  dashboardTrend: DashboardTrendPoint[];
}

export interface FetchDashboardTodaySnapshotQueryResult {
  dashboardTodaySnapshot: DashboardTodaySnapshot;
}

const METRIC_FRAGMENT = `
  value
  previousValue
  deltaPercentage
`;

export const FETCH_DASHBOARD_OVERVIEW_QUERY = gql`
  query FetchDashboardOverview($input: DashboardFilterInput!) {
    dashboardOverview(input: $input) {
      businessHealth {
        totalOrders { ${METRIC_FRAGMENT} }
        totalVolume { ${METRIC_FRAGMENT} }
        volumeBreakdown {
          gallon
          smallpack
        }
        revenue { ${METRIC_FRAGMENT} }
        activeCustomers { ${METRIC_FRAGMENT} }
        returningCustomerRate { ${METRIC_FRAGMENT} }
        timeliness {
          label
          count
          percentage
        }
        timelinessUncalculatedOrders
      }
      branchPerformance {
        branchId
        branchName
        revenue
        orders
        volume
        onTimeRate
        finishedOrders
      }
      generatedAt
    }
  }
`;

export const FETCH_DASHBOARD_TREND_QUERY = gql`
  query FetchDashboardTrend($input: DashboardFilterInput!, $granularity: DashboardTrendGranularity!) {
    dashboardTrend(input: $input, granularity: $granularity) {
      label
      orders
      revenue
    }
  }
`;

export const FETCH_DASHBOARD_TODAY_SNAPSHOT_QUERY = gql`
  query FetchDashboardTodaySnapshot($branchId: Int) {
    dashboardTodaySnapshot(branchId: $branchId) {
      totalScheduledOrders
      deliveredOnTime
      pendingToday
      unfinished
      generatedAt
    }
  }
`;
